import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
    DisplayCurrency,
    USD,
    displayCurrencyForCountry,
    formatInCurrency,
    isUSD,
} from '../../../config/currency';
import { getCachedCountryCode, resolveCountryCode } from '../../../services/geo';

interface DisplayCurrencyValue {
    currency: DisplayCurrency;
    /** Whether the active display currency differs from the USD charge currency. */
    converted: boolean;
    /** Format a USD amount as a display string in the active currency (with symbol). */
    format: (usdAmount: number) => string;
    /** Format the numeric part only (no symbol). */
    formatAmount: (usdAmount: number) => string;
}

function buildValue(currency: DisplayCurrency): DisplayCurrencyValue {
    return {
        currency,
        converted: !isUSD(currency),
        format: (usd) => formatInCurrency(usd, currency),
        formatAmount: (usd) => formatInCurrency(usd, currency, false),
    };
}

const DisplayCurrencyContext = createContext<DisplayCurrencyValue>(buildValue(USD));

/**
 * Resolves the visitor's display currency from their geo-detected country and
 * provides USD->display formatting helpers to the funnel. Defaults to USD and
 * updates once geo resolves; the Stripe charge always remains USD.
 */
export const DisplayCurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currency, setCurrency] = useState<DisplayCurrency>(() =>
        displayCurrencyForCountry(getCachedCountryCode()),
    );

    useEffect(() => {
        let active = true;
        resolveCountryCode().then((code) => {
            if (active) setCurrency(displayCurrencyForCountry(code));
        });
        return () => { active = false; };
    }, []);

    const value = useMemo(() => buildValue(currency), [currency]);

    return (
        <DisplayCurrencyContext.Provider value={value}>
            {children}
        </DisplayCurrencyContext.Provider>
    );
};

export function useDisplayCurrency(): DisplayCurrencyValue {
    return useContext(DisplayCurrencyContext);
}
